import { useState } from 'react';
import { TrackerDataType } from '../../shared/constants/constants';
import './ClientAnalytics.css';
import dayjs from '../../utils/dayjs';
import { Button, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchModal from './SearchModal';
import AnalyticsTable from './AnalyticsTable/AnalyticsTable';
import { chartTypeEnum } from './constants';
import * as chartAPIRequests from '../../shared/requests/teamAnalyticsChartRequests';
import { Spinner } from 'react-bootstrap';
import { inProgressKeys, IssueKeys, ServiceKeys } from '../../shared/constants/IssueAndServiceKeys';
import { useDropdownTable } from '../../shared/hooks/useDropdownTable';
import { AddOnTypes } from '../../shared/constants/TenantFields';
import { customAnalyticsUICopy, getUICopyFieldsForItem } from './customAnalyticsUICopy';
import { getBadgesForCustomAnalytics, shouldHaveCustomAnalyticsBadges } from './customAnalyticsBadges';

const DESCRIPTIVE_BADGE_LABELS = new Set([
  'status',
  'tone',
  'unit',
  'campaign',
  'team member',
  'topic',
  'experts',
  'outlets',
  'engagement log',
  'volume',
  'storytelling',
  'complexity',
  'type',
]);

const useStyles = makeStyles(() => ({
  filterBar: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    marginBottom: '24px',
    justifyContent: 'center',
  },
}));

//hook to use to prevent multiple rerenders when setting multiple states.
///https://stackoverflow.com/questions/59163378/react-hooks-skip-re-render-on-multiple-consecutive-setstate-calls

function CustomAnalytics() {
  const classes = useStyles();

  const toBroadsightEntityTitleCase = (value) => {
    if (typeof value !== 'string') {
      return value;
    }

    return value
      .replaceAll(/\bmedia interactions\b/gi, 'Media Interactions')
      .replaceAll(/\bmedia interaction\b/gi, 'Media Interaction')
      .replaceAll(/\binteractions\b/gi, 'Interactions')
      .replaceAll(/\binteraction\b/gi, 'Interaction')
      .replaceAll(/\bengagements\b/gi, 'Engagements')
      .replaceAll(/\bengagement\b/gi, 'Engagement')
      .replaceAll(/\bservices\b/gi, 'Services')
      .replaceAll(/\bservice\b/gi, 'Service');
  };

  const normalizeAnalyticsCopy = (item) => ({
    ...item,
    title: toBroadsightEntityTitleCase(item.title),
    subTitle: toBroadsightEntityTitleCase(item.subTitle),
    scope: toBroadsightEntityTitleCase(item.scope),
    infoTitle: toBroadsightEntityTitleCase(item.infoTitle),
    infoBody: Array.isArray(item.infoBody)
      ? item.infoBody.map((entry) => toBroadsightEntityTitleCase(entry))
      : item.infoBody,
  });

  const { data } = useDropdownTable({});
  const dropdownTable = data ?? 'loading';
  const interactionsColumns = [
    IssueKeys.entryDate,
    IssueKeys.dueDate,
    IssueKeys.topic,
    IssueKeys.units,
    IssueKeys.outlet,
  ];
  const servicesColumns = [
    ServiceKeys.entryDate,
    ServiceKeys.dueDate,
    ServiceKeys.service,
    IssueKeys.units,
    ServiceKeys.type,
  ];
  const inProgressColumns = [
    inProgressKeys.topic,
    inProgressKeys.outlet,
    inProgressKeys.teamMember,
    inProgressKeys.timeStamp,
  ];
  //The default 90 day filter time frame.
  const now = dayjs().endOf('day').toDate();
  const past = dayjs().startOf('month').subtract(2, 'month').toDate();
  const defaultQuery = `startDate=${past.toISOString()}&endDate=${now.toISOString()}`;

  //using this state setter, the network requests for all chart data will be called with the new query state.
  const [query, setQuery] = useState(defaultQuery);

  //This function gets set to the setOpen state stter in SearchModal on first render, this way we can open/close modal without
  //having to rerender the charts.
  let showModal = () => {};

  //pass in network request for the corresponding backend call
  //This network request will be called in the corresponding useEffect hook of the table/chart component
  //This way we don't have to manage the state for each chart in this component, but rather in subcomponents.
  const returnChartsArrayData = () => {
    const chartItems = [
      {
        title: 'Media Interactions by Type',
        type: chartTypeEnum.ChartJSStackedBar,
        category: 'Work mix',
        queryKey: 'media-interactions-by-type',
        networkRequest: chartAPIRequests.getInteractionsByType,
      },
      {
        title: 'Media Interactions by Team Member',
        type: chartTypeEnum.ChartJSStackedBar, //line
        category: 'Team workload & ownership',
        queryKey: 'media-interactions-by-team-member',
        networkRequest: chartAPIRequests.getInteractionsByMember,
      },
      {
        title: 'Services by Team Member',
        type: chartTypeEnum.ChartJSStackedBar,
        category: 'Team workload & ownership',
        queryKey: 'services-by-team-member',
        networkRequest: chartAPIRequests.getServicesByMember,
      },
      {
        title: 'Proactive vs Reactive Media Interactions',
        plotly: 1,
        type: chartTypeEnum.line,
        category: 'Topics: Proactive focus',
        queryKey: 'proactive-vs-reactive-media-interactions',
        networkRequest: chartAPIRequests.getPvrData,
      },
      {
        title: 'Campaign Distribution',
        plotly: 0,
        type: chartTypeEnum.ChartJSDoughnut, //pie
        category: 'Campaigns, units & organizational reporting',
        queryKey: 'campaign-distribution',
        networkRequest: chartAPIRequests.getCampaignsChartData,
      },
      {
        title: 'Top Media Interaction Topics (Overall)',
        type: chartTypeEnum.ChartJSBar,
        category: 'Topics: Overall',
        queryKey: 'top-media-interaction-topics-overall',
        networkRequest: chartAPIRequests.getTopInteractionTopicsOverall,
        enlargedViewRequest: chartAPIRequests.getMoreInteractionTopicsOverall,
      },
      {
        title: 'Top Media Interaction Topics (Overall)',
        type: chartTypeEnum.wordCloud,
        category: 'Topics: Overall',
        queryKey: 'top-media-interaction-topics-overall-word-cloud',
        networkRequest: chartAPIRequests.get50InteractionTopicsOverall,
      },
      {
        title: 'Top Media Issue Topics',
        type: chartTypeEnum.ChartJSBar,
        category: 'Topics: Issue response',
        queryKey: 'top-media-issue-topics',
        networkRequest: chartAPIRequests.getTopMediaTopics,
        enlargedViewRequest: chartAPIRequests.getMoreMediaTopics,
      },
      {
        title: 'Top Media Interactions by Unit',
        type: chartTypeEnum.ChartJSDoughnut,
        category: 'Campaigns, units & organizational reporting',
        queryKey: 'top-media-interactions-by-unit',
        networkRequest: chartAPIRequests.getInteractionsByUnit,
      },
      {
        title: 'Top Proactive Media Topics',
        type: chartTypeEnum.ChartJSBar,
        category: 'Topics: Proactive focus',
        queryKey: 'top-proactive-media-topics',
        networkRequest: chartAPIRequests.getTopProactiveTopics,
        enlargedViewRequest: chartAPIRequests.getMoreProactiveTopics,
      },
      {
        title: 'Complexity of Services',
        plotly: 1,
        type: chartTypeEnum.PlotlyMonthLine,
        category: 'Team workload & ownership',
        queryKey: 'complexity-of-services',
        networkRequest: chartAPIRequests.getComplexityChartData,
      },
      {
        title: 'Media Interactions Compared to Past Years',
        plotly: 1,
        type: chartTypeEnum.numberBox,
        subTitle: 'Media Interactions in time period',
        category: 'Media landscape & trends',
        queryKey: 'media-interactions-compared-to-past-years',
        networkRequest: chartAPIRequests.getApplesData,
      },
      {
        title: 'Top Service Areas (Unit)',
        type: chartTypeEnum.topHits,
        category: 'Campaigns, units & organizational reporting',
        queryKey: 'top-service-areas-unit',
        networkRequest: chartAPIRequests.getTopServiceHits,
      },
      {
        title: 'Top Media Areas (Unit)',
        type: chartTypeEnum.topHits,
        category: 'Campaigns, units & organizational reporting',
        queryKey: 'top-media-areas-unit',
        networkRequest: chartAPIRequests.getTopMediaHits,
      },
      {
        title: 'Services by Type',
        type: chartTypeEnum.ChartJSStackedBar,
        category: 'Work mix',
        queryKey: 'services-by-type',
        networkRequest: chartAPIRequests.getServicesByType,
      },
      {
        title: 'Top Complex Services',
        type: chartTypeEnum.ChartJSBar,
        label: '# of Total Complexity for Service',
        category: 'Team workload & ownership',
        queryKey: 'top-complex-services',
        networkRequest: chartAPIRequests.getTopComplexServices,
        enlargedViewRequest: chartAPIRequests.getMoreComplexServices,
      },
      {
        title: 'Top 20 Media Outlets',
        plotly: 0,
        type: chartTypeEnum.ChartJSDoughnut, //pie
        category: 'Media landscape & trends',
        queryKey: 'top-20-media-outlets',
        networkRequest: chartAPIRequests.getOutletsChartData,
      },
      {
        title: 'Interactions by Tone',
        plotly: 0,
        type: chartTypeEnum.ChartJSDoughnut, //pie
        category: 'Media landscape & trends',
        queryKey: 'interactions-by-tone',
        networkRequest: chartAPIRequests.getInteractionsByToneChart,
      },
      {
        title: 'Services by Status',
        plotly: 0,
        type: chartTypeEnum.ChartJSDoughnut, //pie
        category: 'Workload & workflow health',
        queryKey: 'services-by-status',
        networkRequest: chartAPIRequests.getServicesByStatusChart,
      },
      {
        title: 'Interactions by Status',
        plotly: 0,
        type: chartTypeEnum.ChartJSDoughnut, //pie
        category: 'Workload & workflow health',
        queryKey: 'interactions-by-status',
        networkRequest: chartAPIRequests.getInteractionsByStatusChart,
      },
      {
        title: 'Top Proactive Media Topics',
        type: chartTypeEnum.wordCloud,
        category: 'Topics: Proactive focus',
        queryKey: 'top-proactive-media-topics-word-cloud',
        networkRequest: chartAPIRequests.get50ProactiveTopics,
      },
      {
        title: 'Top Media Issue Topics',
        type: chartTypeEnum.wordCloud,
        category: 'Topics: Issue response',
        queryKey: 'top-media-issue-topics-word-cloud',
        networkRequest: chartAPIRequests.get50MediaTopics,
      },
      {
        title: 'Top Experts',
        type: chartTypeEnum.wordCloud,
        category: 'Media landscape & trends',
        queryKey: 'top-experts-word-cloud',
        networkRequest: chartAPIRequests.getExperts,
      },
      {
        title: 'Top Engagement Topics (Overall)',
        type: chartTypeEnum.ChartJSBar,
        category: 'Engagement Log insights',
        queryKey: 'top-engagement-topics-overall',
        networkRequest: chartAPIRequests.getTopEngagementTopicsOverall,
        enlargedViewRequest: chartAPIRequests.getMoreEngagementTopicsOverall,
        addOn: AddOnTypes.engagementLog,
      },
      {
        title: 'Top Engagement Topics',
        type: chartTypeEnum.wordCloud,
        category: 'Engagement Log insights',
        queryKey: 'top-engagement-topics-word-cloud',
        networkRequest: chartAPIRequests.getTopEngagementTopicsChart,
        addOn: AddOnTypes.engagementLog,
      },
      {
        title: 'Engagements by Type',
        type: chartTypeEnum.ChartJSStackedBar,
        category: 'Engagement Log insights',
        queryKey: 'engagements-by-type',
        networkRequest: chartAPIRequests.getEngagementsByTypeChart,
        addOn: AddOnTypes.engagementLog,
      },
      {
        title: 'Organizations Engaged',
        type: chartTypeEnum.ChartJSBar,
        label: '# of Engagements per Organization',
        category: 'Engagement Log insights',
        queryKey: 'types-of-organizations-engaged',
        networkRequest: chartAPIRequests.getOrganizationsEngagedChart,
        enlargedViewRequest: chartAPIRequests.getMoreOrganizationsEngagedChart,
        addOn: AddOnTypes.engagementLog,
      },
      {
        title: 'Top Services by Unit',
        type: chartTypeEnum.ChartJSDoughnut,
        category: 'Campaigns, units & organizational reporting',
        queryKey: 'top-services-by-unit',
        networkRequest: chartAPIRequests.getServicesByUnit,
      },
    ];

    return chartItems.map((item) => {
      const uiCopy = getUICopyFieldsForItem(customAnalyticsUICopy.charts, item.queryKey);
      const computedBadges = getBadgesForCustomAnalytics(item.queryKey, item.category, { addOn: item.addOn });

      if (
        process.env.NODE_ENV !== 'production' &&
        shouldHaveCustomAnalyticsBadges(item.queryKey, item.category) &&
        computedBadges.length === 0
      ) {
        console.warn(`Missing badges for custom analytics chart: ${item.queryKey}`);
      }
      if (process.env.NODE_ENV !== 'production' && computedBadges.some((badge) => !badge?.tooltip?.trim())) {
        console.warn(`Missing tooltip on Custom Analytics badge(s) for chart: ${item.queryKey}`);
      }
      if (
        process.env.NODE_ENV !== 'production' &&
        computedBadges.some((badge) => DESCRIPTIVE_BADGE_LABELS.has((badge?.label || '').trim().toLowerCase()))
      ) {
        console.warn(`Descriptive badge still present on Custom Analytics chart: ${item.queryKey}`);
      }

      return normalizeAnalyticsCopy({
        ...item,
        ...uiCopy,
        analyticsVariant: 'custom',
        sectionLabel: '',
        showSectionAccent: false,
        badges: computedBadges,
      });
    });
  };
  const returnTablesDataArray = () => {
    const tableItems = [
      {
        columns: interactionsColumns,
        dropdownTable: dropdownTable,
        title: 'All Media Interactions',
        type: TrackerDataType.MEDIA_INTERACTION,
        category: 'Logs (full view)',
        gridLg: 6,
        queryKey: 'all-media-interactions',
        networkRequest: chartAPIRequests.getInteractionsTable,
      },
      {
        columns: servicesColumns,
        dropdownTable: dropdownTable,
        title: 'All Media Services',
        type: TrackerDataType.SERVICE,
        category: 'Logs (full view)',
        gridLg: 6,
        queryKey: 'all-media-services',
        networkRequest: chartAPIRequests.getServicesTable,
      },
      {
        columns: inProgressColumns,
        dropdownTable: dropdownTable,
        type: TrackerDataType.MEDIA_INTERACTION,
        title: 'Media Interactions in Progress',
        category: 'Workload & workflow health',
        queryKey: 'media-interactions-in-progress',
        networkRequest: chartAPIRequests.getInProgressInteraction,
      },
    ];

    if (process.env.NODE_ENV !== 'production') {
      console.log(
        '[CustomAnalytics tables source queryKeys]',
        tableItems.map((item) => ({
          title: item.title,
          queryKey: item.queryKey,
        }))
      );
    }

    return tableItems.map((item) => {
      const uiCopy = getUICopyFieldsForItem(customAnalyticsUICopy.tables, item.queryKey);
      const computedBadges = getBadgesForCustomAnalytics(item.queryKey, item.category, { addOn: item.addOn });

      if (process.env.NODE_ENV !== 'production' && computedBadges.some((badge) => !badge?.tooltip?.trim())) {
        console.warn(`Missing tooltip on Custom Analytics badge(s) for table: ${item.queryKey}`);
      }
      if (
        process.env.NODE_ENV !== 'production' &&
        computedBadges.some((badge) => DESCRIPTIVE_BADGE_LABELS.has((badge?.label || '').trim().toLowerCase()))
      ) {
        console.warn(`Descriptive badge still present on Custom Analytics table: ${item.queryKey}`);
      }

      return normalizeAnalyticsCopy({
        ...item,
        ...uiCopy,
        badges: computedBadges,
      });
    });
  };

  return (
    <div className="analytics-wrapper">
      <div className={classes.filterBar}>
        <div className="filterButton">
          <Button variant="contained" onClick={() => showModal()} data-cy="custom-analytics-filters-button">
            {' '}
            <Typography>
              <FilterListIcon />
              Filters
            </Typography>
          </Button>
        </div>
        <div className={classes.filters}>
          <SearchModal setModalOpen={(f) => (showModal = f)} dropdownTable={dropdownTable} setQuery={setQuery} />
        </div>
      </div>
      {dropdownTable !== 'loading' ? (
        <AnalyticsTable
          chartDataArray={returnChartsArrayData()}
          tableDataArray={returnTablesDataArray()}
          query={query}
        ></AnalyticsTable>
      ) : (
        <Spinner animation="border"></Spinner>
      )}
    </div>
  );
}

export default CustomAnalytics;
